import { Check, ChevronsUpDown, Search } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import type { ProducerRow } from "./contracts-api";

interface ProducerSelectorProps {
	producers: ProducerRow[];
	value: number | null;
	onChange: (value: number | null) => void;
	placeholder?: string;
	id?: string;
	/** Forward do PopoverContent. Wymagane gdy selector siedzi w Radix Dialog —
	 * inaczej Dialog's FocusScope nie pozwala wpisac w input wyszukiwarki. */
	portalContainer?: HTMLElement | null;
}

export function ProducerSelector({
	producers,
	value,
	onChange,
	placeholder = "Wybierz producenta...",
	id,
	portalContainer,
}: ProducerSelectorProps) {
	const [open, setOpen] = React.useState(false);
	const [searchQuery, setSearchQuery] = React.useState("");

	const filteredProducers = React.useMemo(() => {
		if (!searchQuery) return producers;
		return producers.filter((p) => p.nazwa?.toLowerCase().includes(searchQuery.toLowerCase()));
	}, [producers, searchQuery]);

	const selectedProducer = producers.find((producer) => producer.id === value);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					id={id}
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className="w-full justify-between font-normal h-10 px-3 bg-background border-input hover:bg-background/80"
				>
					<span className="truncate">{selectedProducer?.nazwa || placeholder}</span>
					<ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[400px] p-0 shadow-xl border-border max-md:w-[calc(100vw-1.5rem)]"
				align="start"
				portalContainer={portalContainer}
			>
				<div className="flex flex-col max-h-[400px]">
					<div className="flex items-center border-b px-3 h-10 shrink-0 bg-popover z-10">
						<Search className="mr-2 size-4 shrink-0 opacity-50" />
						<input
							className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
							placeholder="Szukaj producenta..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</div>
					<ScrollArea className="flex-1 overflow-y-auto">
						<div className="p-1">
							{filteredProducers.length === 0 ? (
								<div className="py-6 text-center text-sm text-muted-foreground italic">
									Nie znaleziono producenta.
								</div>
							) : (
								filteredProducers.map((p) => (
									<button
										key={p.id}
										type="button"
										className={cn(
											"relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground transition-colors",
											value === p.id && "bg-primary/10 text-primary font-bold",
										)}
										onClick={() => {
											onChange(p.id);
											setOpen(false);
											setSearchQuery("");
										}}
									>
										<Check
											className={cn("mr-2 size-4 opacity-0", value === p.id && "opacity-100")}
										/>
										<span className="truncate">{p.nazwa}</span>
									</button>
								))
							)}
						</div>
					</ScrollArea>
				</div>
			</PopoverContent>
		</Popover>
	);
}
